/** ICP-000001 — Enterprise Coding Constitution (mirrors pkg/codingconstitution) */

export const ICP_CODING_ID = 'ICP-000001';

export interface TechnologyLockRow {
  category: string;
  technology: string;
  version: string;
  status: string;
}

export interface StandardRuleRow {
  area: string;
  rule: string;
  enforced: boolean;
}

export interface DataAPIEventRow {
  category: string;
  item: string;
  standard: string;
  status: string;
}

export interface AIMarketplaceRow {
  domain: string;
  standard: string;
  risk: string;
}

export interface SecurityLoggingRow {
  category: string;
  control: string;
  status: string;
}

export interface TestingCIRow {
  area: string;
  standard: string;
  coverage: string;
}

export interface ExecutiveComplianceRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const ARCHITECTURE_COMPLIANCE = [
  'MPC-00001', 'FCP', 'BCP', 'FRP', 'APP', 'EDP', 'SDP', 'UXP', 'LEP', 'EGB', 'MDEP', 'MDDM',
];

export const REJECTED_TECHNOLOGIES = [
  'MongoDB as primary store', 'PHP backend', 'jQuery frontend', 'MySQL primary',
  'Unversioned REST', 'Plaintext secrets', 'Unstructured logging',
];

export const BACKEND_STANDARDS = [
  'Package Structure', 'Folder Structure', 'Service Layer', 'Repository Layer', 'Domain Layer',
  'DTO Layer', 'Validation Layer', 'Dependency Injection', 'Error Handling', 'Logging',
  'Transactions', 'Concurrency', 'Context Propagation', 'Middleware', 'Caching', 'Events', 'Workers',
];

export const GO_CODING_RULES = [
  'Naming Rules', 'Packages', 'Interfaces', 'Structs', 'Methods', 'Constants',
  'Error Wrapping', 'Context Usage', 'Testing', 'Benchmarking',
];

export const FRONTEND_STANDARDS = [
  'Pages', 'Layouts', 'Components', 'Hooks', 'Stores', 'Contexts', 'Services', 'API Layer',
  'Authentication', 'Routing', 'Forms', 'Validation', 'State Management', 'Error Boundaries',
  'Performance', 'Accessibility', 'Responsive Design', 'Theme', 'Localization',
];

export const REACT_RULES = [
  'Component Naming', 'Folder Naming', 'File Naming', 'Hooks', 'Props', 'Events', 'State',
  'Effects', 'Memoization', 'Lazy Loading', 'Suspense', 'Testing', 'Accessibility',
];

export const DATABASE_STANDARDS = [
  'Schemas', 'Tables', 'Primary Keys', 'UUID Policy', 'Foreign Keys', 'Indexes',
  'Unique Constraints', 'Check Constraints', 'Views', 'JSONB', 'Partitioning', 'Audit Tables',
  'Migration Naming', 'Rollback Scripts',
];

export const API_STANDARDS = [
  'REST Standards', 'URI Naming', 'Request DTO', 'Response DTO', 'Pagination', 'Filtering',
  'Sorting', 'Versioning', 'Authentication', 'Authorization', 'Rate Limiting', 'OpenAPI', 'Error Responses',
];

export const EVENT_STANDARDS = [
  'Event Naming', 'Versioning', 'Payload Structure', 'Correlation IDs', 'Causation IDs',
  'Idempotency', 'Retry Policy', 'Dead Letter Queue', 'Audit',
];

export const AI_STANDARDS = [
  'Prompt Structure', 'Prompt Versioning', 'DNA Integration', 'Knowledge Integration',
  'Model Selection', 'Confidence Scores', 'Reasoning Metadata', 'Fallback', 'Human Approval', 'Audit',
];

export const MARKETPLACE_STANDARDS = [
  'Amazon', 'Flipkart', 'Myntra', 'AJIO', 'Shopify', 'OAuth', 'Webhooks',
  'Rate Limiting', 'Retries', 'Sync', 'Conflict Resolution',
];

export const SECURITY_STANDARDS = [
  'JWT', 'Password Hashing', 'CSRF', 'CORS', 'Security Headers', 'Secrets', 'Encryption',
  'Tenant Isolation', 'RBAC', 'Audit', 'Input Validation', 'Output Encoding',
];

export const LOGGING_STANDARDS = [
  'Structured Logs', 'JSON Logs', 'Correlation IDs', 'Request IDs', 'Tenant IDs',
  'Marketplace IDs', 'User IDs', 'Audit IDs', 'Trace IDs',
];

export const ERROR_STANDARDS = [
  'Business Errors', 'Validation Errors', 'Infrastructure Errors', 'Marketplace Errors',
  'AI Errors', 'Database Errors', 'HTTP Errors', 'Retry Rules',
];

export const TESTING_STANDARDS = [
  'Unit Tests', 'Integration Tests', 'Contract Tests', 'End-to-End Tests', 'Performance Tests',
  'Security Tests', 'Marketplace Tests', 'AI Tests', 'Coverage Requirements', 'Naming Standards',
];

export const DOCUMENTATION_STANDARDS = [
  'README', 'Architecture', 'API Documentation', 'Database Documentation', 'Configuration',
  'Deployment', 'Testing Guide', 'Runbooks', 'Examples',
];

export const GIT_STANDARDS = [
  'Branch Strategy', 'Commit Format', 'Pull Requests', 'Review Rules', 'Merge Strategy',
  'Versioning', 'Tags', 'Release Branches',
];

export const CICD_STANDARDS = [
  'Build', 'Lint', 'Static Analysis', 'Unit Tests', 'Integration Tests', 'Security Scan',
  'Dependency Scan', 'Docker Build', 'Release', 'Rollback',
];

export const CODE_QUALITY_STANDARDS = [
  'Cyclomatic Complexity', 'Max Function Length', 'Max File Length', 'Max Nesting',
  'Code Duplication Limits', 'Naming Consistency', 'Technical Debt Rules',
];

export const PERFORMANCE_STANDARDS = [
  'API Response Targets', 'Database Query Targets', 'Memory Usage', 'CPU Usage',
  'Concurrency', 'Caching', 'Batch Processing', 'Background Jobs',
];

export const DEFINITION_OF_READY = [
  'Architecture Approved', 'Dependencies Identified', 'Acceptance Criteria Approved',
  'Database Impact Reviewed', 'API Impact Reviewed', 'AI Impact Reviewed',
];

export const DEFINITION_OF_DONE = [
  'Backend Compiles', 'Frontend Compiles', 'Database Migration Tested', 'Unit Tests Pass',
  'Integration Tests Pass', 'Coverage Threshold Achieved', 'Security Review Passed',
  'Performance Validated', 'Accessibility Validated', 'Documentation Complete',
  'OpenAPI Updated', 'Code Review Approved',
];

export const AI_CODE_GENERATION_RULES = [
  'ICP Number', 'Module', 'Architecture Layer', 'Database Objects', 'API Endpoints',
  'Events', 'Dependencies', 'Tests', 'Documentation',
];

export const COMPLIANCE_CHECKLIST = [
  'Technology Lock Compliant', 'Architecture Compliant', 'Security Compliant', 'Testing Compliant',
  'Documentation Compliant', 'OpenAPI Compliant', 'Event Catalog Compliant', 'Migration Compliant',
];

export const ARCHITECTURE_STAGES = [
  'Technology Lock', 'Backend Go', 'Frontend React', 'Database API Event',
  'AI Marketplace', 'Security Logging Error', 'Testing Documentation CI', 'Executive Compliance',
];

export const LIVE_ROUTES: Record<string, string> = {
  engineeringGovernance: '/platform/engineering-governance',
  enterpriseDevOps: '/platform/enterprise-devops',
  enterpriseQuality: '/platform/enterprise-quality',
  masterDependencyMatrix: '/platform/master-dependency-matrix',
};

export const DEMO_TECHNOLOGY_LOCK: TechnologyLockRow[] = [
  { category: 'Backend', technology: 'Go', version: '1.23+', status: 'approved' },
  { category: 'Frontend', technology: 'React + TypeScript + Vite', version: 'React 18+', status: 'approved' },
  { category: 'Frontend', technology: 'TailwindCSS', version: '3.x', status: 'approved' },
  { category: 'Database', technology: 'PostgreSQL', version: '17', status: 'approved' },
  { category: 'Cache', technology: 'Redis', version: '7.x', status: 'approved' },
  { category: 'Auth', technology: 'JWT', version: 'RFC 7519', status: 'approved' },
  { category: 'Testing', technology: 'Go Test + Vitest + Playwright', version: 'latest', status: 'approved' },
  { category: 'Monitoring', technology: 'Prometheus + Grafana', version: '2.x / 11.x', status: 'approved' },
];

export const DEMO_BACKEND_GO: StandardRuleRow[] = [
  { area: 'Package Structure', rule: 'cmd/ internal/domain/ internal/service/ internal/handler/ internal/repository/', enforced: true },
  { area: 'Service Layer', rule: 'Business logic isolated from HTTP and persistence', enforced: true },
  { area: 'Error Handling', rule: 'Wrap errors with context; use typed domain errors', enforced: true },
  { area: 'Context Propagation', rule: 'context.Context as first parameter in all I/O', enforced: true },
  { area: 'Dependency Injection', rule: 'Constructor injection via interfaces', enforced: true },
  { area: 'Go Naming', rule: 'MixedCaps exports; short package names', enforced: true },
];

export const DEMO_FRONTEND_REACT: StandardRuleRow[] = [
  { area: 'Component Naming', rule: 'PascalCase components; co-located tests', enforced: true },
  { area: 'Feature Slicing', rule: 'pages/ components/ hooks/ services/ per feature', enforced: true },
  { area: 'State Management', rule: 'Local state default; context for cross-cutting', enforced: true },
  { area: 'API Layer', rule: 'Typed clients in apps/web/src/api/', enforced: true },
  { area: 'Accessibility', rule: 'WCAG 2.1 AA minimum for all interactive elements', enforced: true },
  { area: 'Lazy Loading', rule: 'Route-level code splitting via React.lazy', enforced: true },
];

export const DEMO_DATA_API_EVENT: DataAPIEventRow[] = [
  { category: 'Database', item: 'UUID primary keys', standard: 'gen_random_uuid() default', status: 'mandatory' },
  { category: 'Database', item: 'Migration naming', standard: 'NNN_service_description.up.sql', status: 'mandatory' },
  { category: 'Database', item: 'Audit tables', standard: 'created_at, updated_at, tenant_id on all tenant tables', status: 'mandatory' },
  { category: 'API', item: 'URI naming', standard: '/api/v1/{resource}/{id}', status: 'mandatory' },
  { category: 'API', item: 'Error responses', standard: 'RFC 7807 problem+json', status: 'mandatory' },
  { category: 'Event', item: 'Event naming', standard: '{domain}.{entity}.{action}', status: 'mandatory' },
];

export const DEMO_AI_MARKETPLACE: AIMarketplaceRow[] = [
  { domain: 'AI Prompt', standard: 'Versioned prompts with DNA and knowledge context', risk: 'medium' },
  { domain: 'AI Model', standard: 'Router selects model; confidence scores required', risk: 'high' },
  { domain: 'AI Approval', standard: 'Human approval for autonomous actions', risk: 'high' },
  { domain: 'Amazon', standard: 'OAuth + SP-API rate limiting with exponential backoff', risk: 'medium' },
  { domain: 'Shopify', standard: 'Webhook HMAC validation + idempotent sync', risk: 'low' },
  { domain: 'Marketplace Sync', standard: 'Conflict resolution via last-write-wins with audit', risk: 'medium' },
];

export const DEMO_SECURITY_LOGGING: SecurityLoggingRow[] = [
  { category: 'Security', control: 'JWT with short-lived access + refresh rotation', status: 'active' },
  { category: 'Security', control: 'Tenant isolation via tenant_id on all queries', status: 'active' },
  { category: 'Security', control: 'RBAC enforced at handler and service layers', status: 'active' },
  { category: 'Logging', control: 'Structured JSON with correlation_id, tenant_id, trace_id', status: 'active' },
  { category: 'Error', control: 'Typed business errors mapped to HTTP status codes', status: 'active' },
  { category: 'Error', control: 'Marketplace errors with retry classification', status: 'active' },
];

export const DEMO_TESTING_CI: TestingCIRow[] = [
  { area: 'Unit Tests', standard: 'Go test + Vitest; table-driven preferred', coverage: '80% min' },
  { area: 'Integration Tests', standard: 'Testcontainers for PostgreSQL and Redis', coverage: 'critical paths' },
  { area: 'E2E Tests', standard: 'Playwright for user journeys', coverage: 'P0 flows' },
  { area: 'Documentation', standard: 'README + OpenAPI + migration docs per module', coverage: 'all modules' },
  { area: 'Git', standard: 'Conventional commits; PR required for main', coverage: '100%' },
  { area: 'CI/CD', standard: 'lint + test + security scan + docker build gates', coverage: 'all PRs' },
];

export const DEMO_EXECUTIVE: ExecutiveComplianceRow[] = [
  { metric: 'Technology Lock Compliance', value: '15/15 approved', status: 'compliant', score: 100 },
  { metric: 'DoR Gates Defined', value: '6 gates', status: 'active', score: 100 },
  { metric: 'DoD Gates Defined', value: '12 gates', status: 'active', score: 100 },
  { metric: 'AI Traceability Rules', value: '9 fields', status: 'mandatory', score: 100 },
  { metric: 'Compliance Checklist Items', value: '8 areas', status: 'enforced', score: 95 },
  { metric: 'Standards Coverage', value: '22 deliverables', status: 'complete', score: 98 },
];

export function buildComplianceDashboard() {
  return {
    technology_areas: DEMO_TECHNOLOGY_LOCK.length,
    backend_standards: BACKEND_STANDARDS.length + GO_CODING_RULES.length,
    frontend_standards: FRONTEND_STANDARDS.length + REACT_RULES.length,
    database_standards: DATABASE_STANDARDS.length + API_STANDARDS.length + EVENT_STANDARDS.length,
    security_standards: SECURITY_STANDARDS.length + LOGGING_STANDARDS.length + ERROR_STANDARDS.length,
    governance_areas: GIT_STANDARDS.length + CICD_STANDARDS.length + CODE_QUALITY_STANDARDS.length + PERFORMANCE_STANDARDS.length,
  };
}
